/**
 * Card — surface container for content. Replaces the scattered
 * `bg-white rounded-2xl shadow-sm border border-gray-200` patterns.
 *
 * Sub-components:
 *   <Card.Header>  — top section with optional title/subtitle/actions row
 *   <Card.Body>    — main content
 *   <Card.Footer>  — bottom border action area
 *
 * Variants:
 *   surface   — default white card (bg-surface)
 *   elevated  — slightly raised (bg-elevated + larger shadow)
 *   bordered  — no shadow, just a border (for inline groupings)
 *
 * Padding presets via the `padding` prop: 'none', 'sm', 'md' (default), 'lg'.
 */

const VARIANTS = {
  surface:  'bg-surface  border border-border shadow-token-sm',
  elevated: 'bg-elevated border border-border shadow-token-md',
  bordered: 'bg-surface  border border-border',
}

const PADDING = {
  none: '',
  sm:   'p-3',
  md:   'p-4 sm:p-5',
  lg:   'p-6 sm:p-7',
}

export default function Card({
  variant = 'surface',
  padding = 'md',
  as: Component = 'div',
  className = '',
  children,
  ...rest
}) {
  return (
    <Component
      className={`rounded-xl ${VARIANTS[variant] || VARIANTS.surface} ${PADDING[padding] || PADDING.md} ${className}`}
      {...rest}
    >
      {children}
    </Component>
  )
}

Card.Header = function CardHeader({ title, subtitle, actions, className = '', children }) {
  if (children) {
    return <div className={`mb-4 ${className}`}>{children}</div>
  }
  return (
    <div className={`flex items-start justify-between gap-3 mb-4 ${className}`}>
      <div className="min-w-0">
        {title && <h3 className="text-base font-semibold text-ink-primary truncate">{title}</h3>}
        {subtitle && <p className="text-xs text-ink-muted mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  )
}

Card.Body = function CardBody({ className = '', children }) {
  return <div className={className}>{children}</div>
}

Card.Footer = function CardFooter({ className = '', children }) {
  return (
    <div className={`mt-4 pt-4 border-t border-border-subtle flex items-center justify-end gap-2 ${className}`}>
      {children}
    </div>
  )
}
